import { FoodMeasure, LABEL_SERVING_MEASURE_NAME } from '@cf-shared/data-model/food';
import { RECIPE_SERVINGS_MEASURE_NAME } from '@cf-shared/data-model/recipe';

export interface RecipeMeasuresResult {
  // what the recipe should store: either exactly one measure, or nothing at all
  measures: FoodMeasure[];
  // true when a measure arrived under some other name and we renamed it back instead of
  // dropping it. The caller reports this so a future debugger can see the repair happened.
  didAdoptStrayMeasure: boolean;
}

export interface NonRecipeMeasuresResult {
  // what a food that is not a recipe should store
  measures: FoodMeasure[];
  // the recipe servings measures that were taken off the food, if there were any. The caller
  // reports these, because a food losing a measure it had stored is not something the user is
  // told about anywhere.
  removedMeasures: FoodMeasure[];
}

// What a serving-sizes list should do with the recipe servings measure when the food's total
// weight changes. The three answers are spelled out as an enum rather than left implicit,
// because "do nothing" is a real answer that used to be missing (see decideServingsMeasureSync).
export enum ServingsMeasureSyncAction {
  /** This list does not belong to a recipe, so it has no servings measure to keep in step. */
  None = 'None',
  /** The recipe does not carry its servings measure yet, so add one holding the new weight. */
  Seed = 'Seed',
  /** The recipe already carries it, so write the new total weight onto the measure. */
  MirrorWeight = 'MirrorWeight',
}

// Decides what the serving-sizes editor should do when the food's total weight changes.
//
// Only a recipe carries the servings measure, so only a recipe's list ever seeds or updates one.
// A plain food's list used to do it too: on every open it invented a measure named
// RECIPE_SERVINGS_MEASURE_NAME, which the food editor then removed again a moment later, because
// a food that is not a recipe may not carry that name. Nothing was lost by the round trip, but
// it meant that removal fired constantly on a measure the editor had just made up — so a
// genuine one, a measure the user really had stored, could not be told apart from it.
export function decideServingsMeasureSync(
  isRecipeList: boolean,
  measures: readonly FoodMeasure[],
): ServingsMeasureSyncAction {
  if (!isRecipeList) {
    return ServingsMeasureSyncAction.None;
  }
  const hasServingsMeasure = measures.some(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME);
  return hasServingsMeasure ? ServingsMeasureSyncAction.MirrorWeight : ServingsMeasureSyncAction.Seed;
}

// Works out what a NON-recipe food's `measures` array should become. The recipe servings measure
// is reserved for recipes — nothing else may carry it — so it is taken off here.
//
// Nothing in the editor offers that name any more (buildMeasurePickerNames filters the reserved
// names out of every source it draws on), so the only way a plain food still carries one is that
// it was saved before the name was reserved. That makes every removal here worth reporting: it
// is a repair on real stored data, and the user is not told it happened.
//
// Unlike resolveRecipeMeasures above, this does NOT modify what it is handed. When there is
// nothing to remove it hands back the very same array, so the common case does not make the
// measures editor rebind its list for no reason.
export function stripRecipeServingsMeasure(incoming: FoodMeasure[]): NonRecipeMeasuresResult {
  const removedMeasures = incoming.filter(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME);
  if (removedMeasures.length === 0) {
    return { measures: incoming, removedMeasures };
  }
  return {
    measures: incoming.filter(measure => measure.name !== RECIPE_SERVINGS_MEASURE_NAME),
    removedMeasures,
  };
}

// Reads how many servings a recipe makes out of its stored measures. Falls back to 1 when the
// recipe has no measure yet, or carries a nonsense count -- a recipe always makes at least one
// serving, and returning 0 here would divide the recipe's weight by zero downstream.
export function getRecipeServingsCount(measures: FoodMeasure[] | undefined): number {
  const stored = measures?.find(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME);
  const servings = stored?.amount;
  return servings && servings > 0 ? servings : 1;
}

// Builds the measure that describes ONE serving of a recipe: the name, an amount of exactly 1,
// and the weight of that single serving.
//
// The amount is 1 on purpose, and it is the part that is easy to get wrong (#4380). Callers
// that show a food card pair this measure's `amount` with its `weight_g` and with
// nutrients scaled to that same weight -- so putting the servings COUNT in `amount` makes the
// card say "20 serving" beside the weight and macros of a single one.
//
// The total weight is allowed to be missing, and a measure still comes back. A recipe whose
// ingredients have all been deleted has a total weight of 0, while its stored measure keeps
// whatever the ingredients last added up to -- so that stored weight is the best answer left.
// Callers must be able to ask for a one-serving measure whatever the recipe's state, because the
// alternative is handing the card the recipe's own stored measure, whose `amount` is the servings
// COUNT (deferred finding df-5a9d776905f9). When no weight is known at all the result carries 0,
// which callers already read as "there is no weight to show".
export function buildRecipeServingMeasure(
  totalWeight_g: number | undefined,
  measures: FoodMeasure[] | undefined,
): FoodMeasure {
  const storedMeasure = measures?.find(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME);
  const knownWeight_g = totalWeight_g || storedMeasure?.weight_g || 0;
  return {
    name: RECIPE_SERVINGS_MEASURE_NAME,
    amount: 1,
    weight_g: knownWeight_g / getRecipeServingsCount(measures),
  };
}

// Works out what a recipe's `measures` array should become, given whatever the serving-sizes
// editor just handed back.
//
// A recipe carries exactly ONE measure, named RECIPE_SERVINGS_MEASURE_NAME.
//
// MODIFIES the measure objects it is handed -- it renames one and overwrites its weight rather
// than copying, so do not pass in objects you still need.
export function resolveRecipeMeasures(
  incoming: FoodMeasure[],
  totalWeight_g: number,
): RecipeMeasuresResult {
  const preparedMeasure = incoming.find(measure => measure.name === RECIPE_SERVINGS_MEASURE_NAME);
  if (preparedMeasure) {
    // sync the weight FORWARD from the authoritative total, never backward. A recipe's total
    // weight comes from its ingredients (updateTotalWeightHandler); the prepared measure's
    // weight is readonly and only mirrors that total. The ingredients and the serving-sizes
    // card live on different edit tabs, so the measure's weight can lag the real total --
    // writing it back onto totalWeight_g would revert a just-edited total.
    preparedMeasure.weight_g = totalWeight_g;
    return { measures: [preparedMeasure], didAdoptStrayMeasure: false };
  }

  // if a measure came in under some other name, adopt it rather than dropping it. Dropping it
  // is what wiped the servings count (#4380). What actually stops that now is the editor no
  // longer letting the name be changed; this branch is the backstop on the data path.
  //
  // skip a measure named LABEL_SERVING_MEASURE_NAME: measures-list hides those from the user
  // (shouldShowMeasure) but still hands the whole array back, so adopting one would take the
  // servings count from a row nobody ever saw.
  const strayMeasure = incoming.find(measure => measure.name !== LABEL_SERVING_MEASURE_NAME);
  if (strayMeasure) {
    strayMeasure.name = RECIPE_SERVINGS_MEASURE_NAME;
    strayMeasure.weight_g = totalWeight_g;
    return { measures: [strayMeasure], didAdoptStrayMeasure: true };
  }

  // nothing came in, so there genuinely is no measure. That is not a loss and nothing is
  // reported -- the serving-sizes editor seeds a fresh measure the next time it opens.
  return { measures: [], didAdoptStrayMeasure: false };
}
